<template>
  <div class="data-capture-container">
    <h2>9 Questions Review</h2>
    <p><strong>Time</strong>: 15 minutes</p>
    <p>
      <strong>What to do:</strong>
    </p>
    <p>
      Next you will complete the 9 Questions Review, with 3 sections: Personal Indicators, Team
      Indicators and Organisational Indicators
    </p>
    <div v-if="!hasSubmitted">
      <form @submit.prevent="onSubmitTaggedTeams" class="data-capture-form">
        <label for="teams-list-input" class="data-capture-label"
          >But first, please list out the team(s) you are a member of at work. Type each team name
          one by one and press <em>Enter</em> after each entry:</label
        >
        <input
          type="text"
          class="data-capture data-capture-input"
          id="teams-list-input"
          placeholder="Team name"
          v-model.trim="teamName"
          data-test="tagged-teams-input"
          @focus="clearErrorMessage"
          @input="clearErrorMessage"
          @keydown.enter.prevent="handleKeyDown"
        />
        <span class="new-line input-guidance">Press enter after each name</span>
        <p>
          Click on which team you consider to be your primary work team (this is the team you will
          complete the Team Indicators on):
        </p>
        <TransitionGroup class="team-tags-container" name="tags" tag="div">
          <DataTag
            v-for="(team, index) in taggedTeams"
            :key="team"
            :isSelected="index === selectedPrimaryTeamIndex"
            @on-tag-click="onDataTagClick(index)"
            data-test="tagged-team"
          >
            <span>{{ team }}</span>
            <button
              type="button"
              class="btn"
              :class="[
                index === selectedPrimaryTeamIndex
                  ? 'btn-close-tag-sm-white'
                  : 'btn-close-tag-sm-black',
              ]"
              @click.stop="onButtonRemoveClick(index)"
              data-test="remove-tag-button"
            />
          </DataTag>
        </TransitionGroup>
        <button
          type="submit"
          @click="onSubmitTaggedTeams"
          class="btn btn-primary btn-lg"
          :disabled="isSubmitDisabled"
          data-test="submit-tagged-teams-button"
        >
          Submit
        </button>
      </form>
      <p v-if="submitErrorMessage" class="error-message" data-test="tagged-teams-error">
        {{ submitErrorMessage }}
      </p>
    </div>
    <div v-else data-test="tagged-teams-success">
      <div class="center-flex">
        <img src="../../assets/Tick-GIF.gif" alt="success" class="success-img" />
      </div>
      <p>Scroll down to begin the review</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch, watchEffect } from "vue";
import axios, { AxiosError } from "axios";
import DataTag from "../data-tag.vue";
import SessionInstanceState from "../sessions/session-instance-state";
import { UserTeamsTagDataApiModel } from "../../api-models";
import { csContext } from "../../services/cs-context";
import { useAxios } from "../../composables/api";

const emit = defineEmits(["on-submit-data"]);

const teamName = ref("");

const isSubmitting = ref(false);
const hasSubmitted = ref(false);
const submitErrorMessage = ref<string | null>(null);

const taggedTeams = ref<string[]>([]);
const selectedPrimaryTeamIndex = ref<number | null>(null);

const NO_PRIMARY_TEAM_ERROR_MESSAGE = "Please select a primary team";

const isSubmitDisabled = computed(() => {
  return selectedPrimaryTeamIndex.value === null || isSubmitting.value;
});

function onDataTagClick(index: number) {
  selectedPrimaryTeamIndex.value = selectedPrimaryTeamIndex.value === index ? null : index;
}

function onButtonRemoveClick(index: number) {
  if (selectedPrimaryTeamIndex.value === index) {
    selectedPrimaryTeamIndex.value = null;
  }
  taggedTeams.value = taggedTeams.value.filter((_, i) => i !== index);
}

function containsOnlyNumbersAndLetters(input: string): boolean {
  const regex = /^[a-zA-Z0-9\s']+$/;
  return regex.test(input);
}

async function onSubmitTaggedTeams() {
  const primaryTeam =
    selectedPrimaryTeamIndex.value !== null
      ? taggedTeams.value[selectedPrimaryTeamIndex.value]
      : null;

  const defaultErrorMessage =
    "There was an error submitting this information, please refresh the page and try again.";

  if (!primaryTeam) {
    submitErrorMessage.value = NO_PRIMARY_TEAM_ERROR_MESSAGE;
    return;
  }

  try {
    await axios.post<UserTeamsTagDataApiModel>("/api/usertagdata/teams", {
      userId: csContext.user.id,
      taggedTeams: taggedTeams.value,
      primaryTaggedTeam: primaryTeam,
    } as UserTeamsTagDataApiModel);
  } catch (err) {
    const error = err as AxiosError;
    const statusCode = error.response?.status;
    isSubmitting.value = false;
    submitErrorMessage.value = statusCode?.toString().startsWith("4")
      ? error.response?.data
      : defaultErrorMessage;
    hasSubmitted.value = false;
    return;
  }

  hasSubmitted.value = true;
  emit("on-submit-data", "postStrengthMapping");
}

function clearErrorMessage() {
  submitErrorMessage.value = null;
}

function addTeamNameToTagList(name: string) {
  if (name.length && !containsOnlyNumbersAndLetters(name.slice(0, -1))) {
    submitErrorMessage.value = "Team name shouldn't contain special characters";
    return;
  }
  const teamAlreadyAddedToList = taggedTeams.value.some(
    (team) => team.toLowerCase() === name.slice(0).toLowerCase(),
  );
  if (!teamAlreadyAddedToList) {
    taggedTeams.value = [...taggedTeams.value, name.slice(0)];
  }
  teamName.value = "";
}

function handleKeyDown(event: KeyboardEvent) {
  if (event.key === "Enter") {
    addTeamNameToTagList(teamName.value);
  }
}

watch(selectedPrimaryTeamIndex, (newValue) => {
  if (newValue !== null && submitErrorMessage.value === NO_PRIMARY_TEAM_ERROR_MESSAGE) {
    submitErrorMessage.value = null;
  }
});

onBeforeUnmount(() => {
  SessionInstanceState.set("teamName", teamName.value);
  SessionInstanceState.set("taggedTeams", taggedTeams.value);
  SessionInstanceState.set("selectedPrimaryTeamIndex", selectedPrimaryTeamIndex.value);
});

onMounted(() => {
  teamName.value = SessionInstanceState.get("teamName") ?? "";
  taggedTeams.value = SessionInstanceState.get("taggedTeams") ?? [];
  selectedPrimaryTeamIndex.value = SessionInstanceState.get("selectedPrimaryTeamIndex") ?? null;
});

function isNotEmptyString(str: string | null | undefined): boolean {
  return str !== null && str !== undefined && str.trim() !== "";
}

const { data } = useAxios<UserTeamsTagDataApiModel>("/api/usertagdata/getteamdata");

watchEffect(() => {
  if (!hasSubmitted.value && isNotEmptyString(data.value?.primaryTaggedTeam)) {
    hasSubmitted.value = true;
    emit("on-submit-data", "postStrengthMapping");
  }
});
</script>

<style scoped lang="less">
@import "../../styles/cms-data-capture/data-capture.less";

.new-line {
  display: block;
}

.input-guidance {
  color: @form-text-color;
  font-size: small;
  margin-bottom: 10px;
}

.data-capture-input {
  margin-bottom: 5px;
}

.team-tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  margin-bottom: 2rem;
  max-width: 600px;

  @media (max-width: @screen-sm-max) {
    max-width: unset;
  }
}

.tags-move,
.tags-enter-active,
.tags-leave-active {
  transition: all 0.5s ease;
}

.tags-enter-from,
.tags-leave-to {
  opacity: 0;
}

/* ensure leaving items are taken out of layout flow so that moving
   animations can be calculated correctly. */
.tags-leave-active {
  position: absolute;
}
</style>
